/* -------------------------------------------------------------------------- */
/*  Shared helpers                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Collects radio-input option groups from a container.
 * @returns {Map<string, { selectedValue: string | null }>}
 */
function collectOptionGroups(container) {
  const groups = new Map();
  container.querySelectorAll('input[type="radio"]').forEach((input) => {
    const name = input.getAttribute('name');
    if (!name) return;
    if (!groups.has(name)) groups.set(name, { selectedValue: null });
    if (input.checked) groups.get(name).selectedValue = input.getAttribute('label');
  });
  return groups;
}

function areAllOptionsSelected(optionGroups) {
  for (const [, g] of optionGroups) {
    if (!g.selectedValue) return false;
  }
  return true;
}

function getSelectedValues(optionGroups) {
  return Array.from(optionGroups.values())
    .filter((g) => g.selectedValue)
    .map((g) => g.selectedValue);
}

/* -------------------------------------------------------------------------- */
/*  ProductCardHorizontal                                                     */
/* -------------------------------------------------------------------------- */

class ProductCardHorizontal extends HTMLElement {
  constructor() {
    super();
    this.onOptionChanged = this.onOptionChanged.bind(this);
  }

  connectedCallback() {
    this.addEventListener('change', this.onOptionChanged);
    this.onOptionChanged();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onOptionChanged);
  }

  get card() {
    return this.querySelector('.horizontal-product-card');
  }

  /** Whether the product has at least one available variant (server-side). */
  get isProductAvailable() {
    return this.card?.getAttribute('data-product-available') !== 'false';
  }

  /** Parsed variants array (cached after first access). */
  get variants() {
    if (!this._parsedVariants) {
      try {
        this._parsedVariants = JSON.parse(this.card?.getAttribute('data-variants') || '[]');
      } catch {
        this._parsedVariants = [];
      }
    }
    return this._parsedVariants;
  }

  onOptionChanged() {
    if (!this.card) return;

    // Product completely OOS — nothing to compute
    if (!this.isProductAvailable) {
      this._selectedVariant = null;
      this._dispatchEvents(null, null, false);
      return;
    }

    const optionGroups = collectOptionGroups(this.querySelector('.options-container') || this);
    this._updateCrossOptionAvailability(optionGroups);

    const selectedValues = getSelectedValues(optionGroups);
    const allSelected = areAllOptionsSelected(optionGroups);
    const previousVariant = this._selectedVariant || null;

    // Find the matching variant
    let selectedVariant = null;
    if (allSelected && this.variants.length && selectedValues.length) {
      selectedVariant =
        this.variants.find(
          (v) => v.options?.length === selectedValues.length && selectedValues.every((opt) => v.options.includes(opt)),
        ) || null;

      // Fallback: match by title
      if (!selectedVariant) {
        const title = selectedValues.join(' / ');
        selectedVariant = this.variants.find((v) => v.title === title || v.public_title === title) || null;
      }
    }

    this._selectedVariant = selectedVariant;

    // Update hidden input
    const hiddenInput = this.querySelector('.selected-variant-id');
    if (hiddenInput) {
      hiddenInput.value = selectedVariant?.id ?? '';
      hiddenInput.setAttribute('data-available', selectedVariant?.available ? 'true' : 'false');
    }

    // Update product links
    if (selectedVariant) {
      this.querySelectorAll('a[href^="/products/"]').forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('variant', selectedVariant.id);
        link.href = url.pathname + url.search + url.hash;
      });
    }

    this._dispatchEvents(selectedVariant, previousVariant, allSelected);
  }

  /**
   * For each option group, marks individual values as `is-disabled` when no
   * available variant exists that combines that value with the currently
   * selected values of all *other* option groups.
   *
   * This lets the customer see which options are available **before** selecting
   * them, regardless of which group they interact with first.
   */
  _updateCrossOptionAvailability(optionGroups) {
    const entries = Array.from(optionGroups.entries());

    entries.forEach(([groupName, ,], groupIndex) => {
      // Selected values from every OTHER option group (skip unselected ones)
      const otherSelections = entries
        .filter((_, i) => i !== groupIndex)
        .map(([, g]) => g.selectedValue)
        .filter(Boolean);

      this.querySelectorAll(`input[name="${groupName}"]`).forEach((input) => {
        const label = input.id ? this.querySelector(`label[for="${input.id}"]`) : null;
        if (!label) return;

        const optionValue = input.getAttribute('label');

        // Is there at least one available variant that includes this value
        // AND matches all already-selected values from other groups?
        const isAvailable = this.variants.some(
          (v) =>
            v.available && v.options.includes(optionValue) && otherSelections.every((sel) => v.options.includes(sel)),
        );

        label.classList.toggle('is-disabled', !isAvailable);
      });
    });
  }

  _dispatchEvents(selectedVariant, previousVariant, allSelected) {
    this.dispatchEvent(
      new CustomEvent('variant-changed', {
        bubbles: true,
        detail: {
          hasSelectedVariant: !!(selectedVariant && allSelected),
          allRequiredOptionsSelected: allSelected,
        },
      }),
    );
    this.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: selectedVariant, previousVariant },
      }),
    );
  }
}

if (!window.customElements.get('product-card-horizontal')) {
  window.customElements.define('product-card-horizontal', ProductCardHorizontal);
}

/* -------------------------------------------------------------------------- */
/*  BundleAvailability                                                        */
/*  Wraps the ATC area and reacts to variant-changed events to toggle the     */
/*  sold-out / unavailable state for the whole bundle.                        */
/* -------------------------------------------------------------------------- */

class BundleAvailability extends HTMLElement {
  connectedCallback() {
    this._onVariantChanged = this._updateAvailability.bind(this);
    document.addEventListener('variant-changed', this._onVariantChanged);
    this._updateAvailability();
  }

  disconnectedCallback() {
    document.removeEventListener('variant-changed', this._onVariantChanged);
  }

  /** Server-side flag: `false` when any bundled product is completely OOS. */
  get isBundleAvailable() {
    return this.getAttribute('data-bundle-available') !== 'false';
  }

  _updateAvailability() {
    // Static OOS (set server-side) — no JS toggle needed
    if (!this.isBundleAvailable) return;

    const cards = document.querySelectorAll('.horizontal-product-card');
    let hasUnavailableVariant = false;

    cards.forEach((card) => {
      const input = card.querySelector('.selected-variant-id');
      if (input?.value && input.getAttribute('data-available') === 'false') {
        hasUnavailableVariant = true;
      }
    });

    const atcEl = this.querySelector('.bundle-availability__atc');
    const msgEl = this.querySelector('.bundle-availability__message');

    this.classList.toggle('is-variant-unavailable', hasUnavailableVariant);

    if (msgEl) {
      if (hasUnavailableVariant) {
        msgEl.removeAttribute('hidden');
      } else {
        msgEl.setAttribute('hidden', '');
      }
    }
  }
}

if (!window.customElements.get('bundle-availability')) {
  window.customElements.define('bundle-availability', BundleAvailability);
}

/* -------------------------------------------------------------------------- */
/*  AddABundleToCartButton                                                    */
/*  Standalone bundle-to-cart handler (used when the ATC is a native form     */
/*  rather than the React-based product-customization-add-to-cart).           */
/* -------------------------------------------------------------------------- */

class AddABundleToCartButton extends HTMLElement {
  constructor() {
    super();
    this.onVariantChanged = this.onVariantChanged.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this._addListeners();
    this._updateButtonState();
  }

  disconnectedCallback() {
    document.removeEventListener('variant-changed', this.onVariantChanged);
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  onVariantChanged() {
    this._updateButtonState();
  }

  /* ---- Button state ---- */

  _updateButtonState() {
    const cards = document.querySelectorAll('.horizontal-product-card');
    let hasValidProducts = false;
    let hasUnavailable = false;

    cards.forEach((card) => {
      const input = card.querySelector('.selected-variant-id');
      if (!input) return;

      // Check if product is completely OOS
      if (card.getAttribute('data-product-available') === 'false') {
        hasUnavailable = true;
        return;
      }

      // Check selected variant availability
      if (input.value && input.getAttribute('data-available') === 'false') {
        hasUnavailable = true;
        return;
      }

      const optionGroups = collectOptionGroups(card);
      if (input.value && areAllOptionsSelected(optionGroups) && !hasUnavailable) {
        hasValidProducts = true;
      }
    });

    if (hasUnavailable) hasValidProducts = false;

    const button = this.querySelector('button[type="submit"]') || this;
    button.disabled = !hasValidProducts;
    button.style.opacity = hasValidProducts ? '1' : '0.5';
    button.style.cursor = hasValidProducts ? 'pointer' : 'not-allowed';
  }

  /* ---- Bundle generation ---- */

  _generateBundle() {
    const cards = document.querySelectorAll('.horizontal-product-card');
    const items = [];

    cards.forEach((card) => {
      const input = card.querySelector('.selected-variant-id');
      const optionGroups = collectOptionGroups(card);
      const variantId = input?.value;

      if (variantId && areAllOptionsSelected(optionGroups)) {
        items.push({ id: variantId, quantity: 1 });
      }
    });

    if (!items.length) {
      alert(this.dataset.selectMessage || 'Please select required options');
      return null;
    }

    const bundleId = Math.floor(Date.now() / 1000);
    const masterVariantId = window.themeVariables.bundle?.bundle_master_variant_id;
    const parentGid = masterVariantId ? `gid://shopify/ProductVariant/${masterVariantId}` : '';
    const bundlePercentageDecrease = window.themeVariables.bundle?.bundle_discount;

    items.forEach((item) => {
      item.properties = {
        _bundleId: bundleId,
        _bundleMaster: false,
        _parentVariantId: parentGid,
        ...(bundlePercentageDecrease != null ? { _bundlePercentageDecrease: bundlePercentageDecrease } : {}),
      };
    });

    return items;
  }

  /* ---- Listeners ---- */

  _addListeners() {
    document.addEventListener('variant-changed', this.onVariantChanged);
    const form = this.closest('form') || this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.onSubmit);
      this.form = form;
    }
    document.addEventListener('cart:error', this._showErrorMessage.bind(this));
  }

  _showErrorMessage(event) {
    this.querySelectorAll('.banner--error').forEach((b) => b.remove());
    const banner = document.createRange().createContextualFragment(`
      <div class="banner banner--error" role="alert">${event.detail.error}</div>
    `).firstElementChild;
    this.prepend(banner);
    setTimeout(() => banner.remove(), 21500);
  }

  /* ---- Cart ---- */

  async _addBundleToCart(bundledItems) {
    let responseJson;
    try {
      this.disabled = true;
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }),
      );

      const searchParams = new URLSearchParams();
      if (sectionsToBundle.length) searchParams.append('sections', sectionsToBundle.join(','));
      const url = `/cart/add.js${sectionsToBundle.length ? '?' + searchParams : ''}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: bundledItems }),
      });

      responseJson = await response.json();
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      if (!response.ok) throw new Error('Failed to add items to cart');

      if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      if (responseJson.sections) cartContent.sections = responseJson.sections;

      this.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.items ?? [responseJson],
            cart: cartContent,
          },
        }),
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent },
        }),
      );
    } catch (e) {
      this.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: responseJson?.description || e.message },
        }),
      );
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      this._updateButtonState();
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      this.disabled = false;
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    const button = this.querySelector('button[type="submit"]') || this;
    if (button.disabled) return;
    const items = this._generateBundle();
    if (items) await this._addBundleToCart(items);
  }
}

if (!window.customElements.get('bundle-add-button')) {
  window.customElements.define('bundle-add-button', AddABundleToCartButton);
}
